import { lazy } from 'react';

// constants
import LocalStrings from './assets/languages';
import appIcons from './constants/appIcons';
import { ALLOWED_FEATURE_KEYS } from './constants/section-constants/common-constants';
import { APPTABID_ROUTE_PATTERN } from './constants/section-constants/redirection-constants';
import quickLinkIcons from './assets/img/quick-links/index';

// --------- LAZY COMPONENTS
const LazyEventOverView = lazy(() => import('./components/event-overview'));
const LazySessions = lazy(() => import('./components/sessions/components'));
const LazyMyTickets = lazy(() => import('./components/mytickets'));
const LazyGallery = lazy(() => import('./components/gallery'));
const LazySurvey = lazy(() => import('./components/survey'));
const LazyAgenda = lazy(() => import('./components/agenda'));
const LazyAutoLogin = lazy(() => import('./components/autologin'));
const LazySSOAuth = lazy(() => import('./components/autologin/sso-auth'));
const LazyMyProfile = lazy(() => import('./components/myprofile'));
const LazyNews = lazy(() => import('./components/news/components'));
const LazyNewsDetails = lazy(() =>
  import('./components/news/components/newsdetails')
);
const LazyUsersChat = lazy(() => import('./components/userschat/components'));
const LazyAttendee = lazy(() => import('./components/attendee/components'));
const LazyExhibitors = lazy(() => import('./components/exhibitors'));
const LazySponsors = lazy(() => import('./components/sponsors'));
const LazySpeakers = lazy(() => import('./components/speakers/components'));
const LazyBookmarks = lazy(() => import('./components/bookmarks/components'));
const LazyFourZeroFour = lazy(() => import('./components/Common/pagenotfound'));
const LazyCommunityForum = lazy(() =>
  import('./components/community-forum/components')
);
const LazySocial = lazy(() => import('./components/social/components'));
const LazyGamification = lazy(() =>
  import('./components/gamification/components')
);
const LazyMaps = lazy(() => import('./components/maps'));
const LazyNotes = lazy(() => import('./components/notes/components'));
const LazyEventWebview = lazy(() => import('./components/event-webview'));
const LazyMapDetails = lazy(() =>
  import('./components/maps/components/mapdetails')
);
const LazyEventGuide = lazy(() => import('./components/event-guide'));
const LazyNotifications = lazy(() =>
  import('./components/notifications/components')
);
const LazyGlobalSearch = lazy(() =>
  import('./components/global-search/components')
);
const LazyMyScans = lazy(() => import('./components/myscans/components'));
const LazyAnalytics = lazy(() => import('./components/Common/analytics'));
const LazyProfileExhibitor = lazy(() =>
  import('./components/myprofile/components/exhibitor')
);
const LazyProfileSponsor = lazy(() =>
  import('./components/myprofile/components/sponsor')
);
const LazyProfileSpeaker = lazy(() =>
  import('./components/myprofile/components/speaker')
);
const LazyMeetings = lazy(() => import('./components/meetings'));
const LazyItinerary = lazy(() => import('./components/itinerary'));

export const commonRoutes = [
  {
    path: '/overview',
    name: LocalStrings.dynamic.kOverviewText,
    languageKey: 'kOverviewText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.homeIcon,
    darkIcon: appIcons.homeIcon,
    component: LazyEventOverView,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: true,
    key: ALLOWED_FEATURE_KEYS.overview
  },
  {
    path: '/myprofile',
    name: '',
    languageKey: '',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: null,
    darkIcon: null,
    component: LazyMyProfile,
    display: false,
    position: '',
    section: '',
    hasAccess: true,
    key: ALLOWED_FEATURE_KEYS.profile.personal
  },
  {
    path: '/myprofile-exhibitor',
    name: '',
    languageKey: '',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: null,
    darkIcon: null,
    component: LazyProfileExhibitor,
    display: false,
    position: '',
    section: '',
    hasAccess: true,
    key: ALLOWED_FEATURE_KEYS.profile.exhibitor
  },
  {
    path: '/myprofile-sponsor',
    name: '',
    languageKey: '',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: null,
    darkIcon: null,
    component: LazyProfileSponsor,
    display: false,
    position: '',
    section: '',
    hasAccess: true,
    key: ALLOWED_FEATURE_KEYS.profile.sponsor
  },
  {
    path: '/myprofile-speaker',
    name: '',
    languageKey: '',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: null,
    darkIcon: null,
    component: LazyProfileSpeaker,
    display: false,
    position: '',
    section: '',
    hasAccess: true,
    key: ALLOWED_FEATURE_KEYS.profile.speaker
  },
  {
    path: '/404',
    name: '',
    languageKey: '',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: '',
    darkIcon: '',
    component: LazyFourZeroFour,
    display: false,
    hasAccess: true,
    section: '',
    position: ''
  },
  {
    path: '/autologin',
    name: '',
    languageKey: '',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: '',
    darkIcon: '',
    component: LazyAutoLogin,
    display: false,
    section: '',
    position: '',
    hasAccess: true
  },
  {
    path: '/sso-auth',
    name: '',
    languageKey: '',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: '',
    darkIcon: '',
    component: LazySSOAuth,
    display: false,
    section: '',
    position: '',
    hasAccess: true
  },
  {
    path: '/mytickets',
    name: LocalStrings.dynamic.kMyTicketsText,
    languageKey: 'kMyTicketsText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.ticketIcon,
    darkIcon: appIcons.ticketIcon,
    component: LazyMyTickets,
    display: true,
    position: 'top',
    section: 'side',
    hasAccess: true,
    key: ALLOWED_FEATURE_KEYS.tickets
  },
  {
    path: '/globalsearch',
    name: '',
    languageKey: '',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: '',
    darkIcon: '',
    component: LazyGlobalSearch,
    display: true,
    position: '',
    section: '',
    hasAccess: true,
    key: ALLOWED_FEATURE_KEYS.globalsearch
  },
  {
    path: '/bookmarked-newsdetails',
    name: '',
    languageKey: '',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: '',
    darkIcon: '',
    component: LazyNewsDetails,
    display: false,
    position: '',
    hasAccess: false,
    section: ''
  }
];

/**
 * section: side => side bar top menu, menu => profile DD item
 */

export const permissionRoutes = [
  {
    path: '/notes',
    name: LocalStrings.dynamic.kNotesText,
    languageKey: 'kNotesText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.notesIcon,
    darkIcon: appIcons.notesIcon,
    component: LazyNotes,
    display: true,
    position: 'top',
    section: 'side',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.notes,
    quicklinkIcon: quickLinkIcons.light.notes,
    darkQuicklinkIcon: quickLinkIcons.dark.notes
  },
  {
    path: '/notifications',
    name: LocalStrings.dynamic.kNotificationsText,
    languageKey: 'kNotificationsText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: '',
    darkIcon: '',
    component: LazyNotifications,
    display: true,
    position: '',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.notifications,
    quicklinkIcon: quickLinkIcons.light.notifications,
    darkQuicklinkIcon: quickLinkIcons.dark.notifications
  },
  {
    path: '/bookmarks',
    name: LocalStrings.dynamic.kBookmarkText,
    languageKey: 'kBookmarkText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.bookmarksIcon,
    darkIcon: appIcons.bookmarksIcon,
    component: LazyBookmarks,
    display: true,
    position: 'top',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.bookmark,
    section: 'menu',
    quicklinkIcon: quickLinkIcons.light.bookmarks,
    darkQuicklinkIcon: quickLinkIcons.dark.bookmarks
  },
  {
    path: '/agenda',
    name: LocalStrings.dynamic.kMyAgenda,
    languageKey: 'kMyAgenda',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.myagendaIcon,
    darkIcon: appIcons.myagendaIcon,
    component: LazyAgenda,
    display: true,
    position: 'top',
    section: 'menu',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.agenda
  },
  {
    path: `/sessions${APPTABID_ROUTE_PATTERN}`,
    // path: '/sessions',
    name: LocalStrings.dynamic.kSessionsText,
    languageKey: 'kSessionsText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.sessionsIcon,
    darkIcon: appIcons.sessionsIcon,
    component: LazySessions,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.schedule,
    quicklinkIcon: quickLinkIcons.light.schedule,
    darkQuicklinkIcon: quickLinkIcons.dark.schedule
  },
  {
    path: `/exhibitors${APPTABID_ROUTE_PATTERN}`,
    // path: '/exhibitors',
    name: LocalStrings.dynamic.kExhibitorText,
    languageKey: 'kExhibitorText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.exhibitorsIcon,
    darkIcon: appIcons.exhibitorsIcon,
    component: LazyExhibitors,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.exhibitor,
    quicklinkIcon: quickLinkIcons.light.exhibitors,
    darkQuicklinkIcon: quickLinkIcons.dark.exhibitors
  },
  {
    path: `/sponsors${APPTABID_ROUTE_PATTERN}`,
    // path: '/sponsors',
    name: LocalStrings.dynamic.kSponsorText,
    languageKey: 'kSponsorText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.sponsorsIcon,
    darkIcon: appIcons.sponsorsIcon,
    component: LazySponsors,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.sponsor,
    quicklinkIcon: quickLinkIcons.light.sponsors,
    darkQuicklinkIcon: quickLinkIcons.dark.sponsors
  },
  {
    path: `/speakers${APPTABID_ROUTE_PATTERN}`,
    // path: '/speakers',
    name: LocalStrings.dynamic.kSpeakerText,
    languageKey: 'kSpeakerText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.speakersIcon,
    darkIcon: appIcons.speakersIcon,
    component: LazySpeakers,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.speaker,
    quicklinkIcon: quickLinkIcons.light.speakers,
    darkQuicklinkIcon: quickLinkIcons.dark.speakers
  },
  {
    path: `/news${APPTABID_ROUTE_PATTERN}`,
    // path: '/news',
    name: LocalStrings.dynamic.kNewsText,
    languageKey: 'kNewsText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.newsIcon,
    darkIcon: appIcons.newsIcon,
    component: LazyNews,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.news,
    quicklinkIcon: quickLinkIcons.light.news,
    darkQuicklinkIcon: quickLinkIcons.dark.news
  },
  {
    path: '/newsdetails',
    name: '',
    languageKey: '',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: '',
    darkIcon: '',
    component: LazyNewsDetails,
    display: false,
    section: '',
    position: '',
    key: ALLOWED_FEATURE_KEYS.news
  },
  {
    path: `/chat${APPTABID_ROUTE_PATTERN}`,
    // path: '/chat',
    name: LocalStrings.dynamic.kChat,
    languageKey: 'kChat',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.chatIcon,
    darkIcon: appIcons.chatIcon,
    component: LazyUsersChat,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.message,
    quicklinkIcon: quickLinkIcons.light.messages,
    darkQuicklinkIcon: quickLinkIcons.dark.messages
  },
  {
    path: `/attendee${APPTABID_ROUTE_PATTERN}`,
    // path: '/attendee',
    name: LocalStrings.dynamic.kAttendeeText,
    languageKey: 'kAttendeeText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.profileIcon,
    darkIcon: appIcons.profileIcon,
    component: LazyAttendee,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.attendee,
    quicklinkIcon: quickLinkIcons.light.attendees,
    darkQuicklinkIcon: quickLinkIcons.dark.attendees
  },
  {
    path: `/community-forum${APPTABID_ROUTE_PATTERN}`,
    // path: '/community-forum',
    name: LocalStrings.dynamic.kCommunityText,
    languageKey: 'kCommunityText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.communityIcon,
    darkIcon: appIcons.communityIcon,
    component: LazyCommunityForum,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.community,
    quicklinkIcon: quickLinkIcons.light.community,
    darkQuicklinkIcon: quickLinkIcons.dark.community
  },
  {
    path: `/social${APPTABID_ROUTE_PATTERN}`,
    // path: '/social',
    name: LocalStrings.dynamic.kSocialText,
    languageKey: 'kSocialText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.socialIcon,
    darkIcon: appIcons.socialIcon,
    component: LazySocial,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.social,
    quicklinkIcon: quickLinkIcons.light.social,
    darkQuicklinkIcon: quickLinkIcons.dark.social
  },
  {
    path: `/gamification${APPTABID_ROUTE_PATTERN}`,
    // path: '/gamification',
    name: LocalStrings.dynamic.kGamificationsText,
    languageKey: 'kGamificationsText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.gamificationIcon,
    darkIcon: appIcons.gamificationIcon,
    component: LazyGamification,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.gamification,
    quicklinkIcon: quickLinkIcons.light.gamification,
    darkQuicklinkIcon: quickLinkIcons.dark.gamification
  },
  {
    path: `/gallery${APPTABID_ROUTE_PATTERN}`,
    // path: '/gallery',
    name: LocalStrings.dynamic.kGallery,
    languageKey: 'kGallery',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.galleryIcon,
    darkIcon: appIcons.galleryIcon,
    component: LazyGallery,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.gallery,
    quicklinkIcon: quickLinkIcons.light.gallery,
    darkQuicklinkIcon: quickLinkIcons.dark.gallery
  },
  {
    path: `/survey${APPTABID_ROUTE_PATTERN}`,
    // path: '/survey',
    name: LocalStrings.dynamic.kSurveyText,
    languageKey: 'kSurveyText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.surveyIcon,
    darkIcon: appIcons.surveyIcon,
    component: LazySurvey,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.survey,
    quicklinkIcon: quickLinkIcons.light.survey,
    darkQuicklinkIcon: quickLinkIcons.dark.survey
  },
  {
    path: `/webview${APPTABID_ROUTE_PATTERN}`,
    // path: '/webview',
    name: LocalStrings.dynamic.kWebviewText,
    languageKey: 'kWebviewText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.webviewIcon,
    darkIcon: appIcons.webviewIcon,
    component: LazyEventWebview,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.webview,
    quicklinkIcon: quickLinkIcons.light.webview,
    darkQuicklinkIcon: quickLinkIcons.dark.webview
  },
  {
    path: `/maps${APPTABID_ROUTE_PATTERN}`,
    // path: '/maps',
    name: LocalStrings.dynamic.kMap,
    languageKey: 'kMap',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.mapsIcon,
    darkIcon: appIcons.mapsIcon,
    component: LazyMaps,
    display: true,
    position: 'leftside',
    section: '',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.maps,
    quicklinkIcon: quickLinkIcons.light.maps,
    darkQuicklinkIcon: quickLinkIcons.dark.maps
  },
  {
    path: '/mapdetails',
    name: '',
    languageKey: '',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: '',
    darkIcon: '',
    component: LazyMapDetails,
    display: false,
    section: '',
    position: '',
    key: ALLOWED_FEATURE_KEYS.maps
  },
  {
    path: `/event-guide${APPTABID_ROUTE_PATTERN}`,
    // path: '/event-guide',
    name: LocalStrings.dynamic.kEventguideText,
    languageKey: 'kEventguideText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.eventGuideIcon,
    darkIcon: appIcons.eventGuideIcon,
    component: LazyEventGuide,
    display: true,
    position: 'leftside',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.eventguide,
    section: 'menu',
    quicklinkIcon: quickLinkIcons.light.about,
    darkQuicklinkIcon: quickLinkIcons.dark.about
  },
  {
    path: '/myscans',
    name: LocalStrings.dynamic.kMyScansTitleText,
    languageKey: 'kMyScansTitleText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.qrScanIcon,
    darkIcon: appIcons.qrScanIcon,
    component: LazyMyScans,
    display: true,
    position: 'top',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.scans,
    section: 'menu'
  },
  {
    path: '/meetings',
    name: LocalStrings.dynamic.kMeetingsText,
    languageKey: 'kMeetingsText',
    iconType: 'svg',
    darkIconType: 'svg',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: appIcons.calenderIcon,
    darkIcon: appIcons.calenderIcon,
    component: LazyMeetings,
    display: true,
    position: 'leftside',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.meetings,
    section: '',
    quicklinkIcon: quickLinkIcons.light.meetings,
    darkQuicklinkIcon: quickLinkIcons.dark.meetings
  },
  {
    path: '/analytics',
    name: LocalStrings.dynamic.kAnalytics,
    languageKey: 'kAnalytics',
    iconType: '',
    quicklinkIconType: '',
    icon: '',
    component: LazyAnalytics,
    display: false,
    position: '',
    hasAccess: true,
    key: ALLOWED_FEATURE_KEYS.analytics,
    section: ''
  },
  {
    path: '/itinerary',
    name: LocalStrings.dynamic.kMyItinerary,
    languageKey: 'kMyItinerary',
    iconType: 'fa-icon',
    darkIconType: 'fa-icon',
    quicklinkIconType: 'svg',
    darkQuicklinkIconType: 'svg',
    icon: 'fa-solid fa-clipboard-list',
    darkIcon: 'fa-solid fa-clipboard-list',
    component: LazyItinerary,
    display: true,
    position: 'leftside',
    hasAccess: false,
    key: ALLOWED_FEATURE_KEYS.itinerary,
    section: '',
    quicklinkIcon: quickLinkIcons.light.itinerary,
    darkQuicklinkIcon: quickLinkIcons.dark.itinerary
  }
];

export const allRoutes = [...commonRoutes, ...permissionRoutes];
